"use client";

import React, { ReactNode } from "react";
import { CalendarDays, CalendarSync, CircleDollarSign, Headphones, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { excursionTerms } from "@/lib/excursionTerms";
interface HelpTopic {
  title: string;
  desc: ReactNode;
  linkText: string;
  to: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const terms = excursionTerms.en;
const helpTopics: HelpTopic[] = [{
  title: "Booking a Transfer",
  desc: <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
        Learn how to request a transfer online, choose a suitable vehicle, and receive your booking confirmation.
      </p>,
  linkText: "Booking Guide & Details",
  to: "/BookYourPrivateTransferPage",
  icon: CalendarDays
}, {
  title: "Changing or Cancelling",
  desc: <div className="space-y-3 text-sm sm:text-base leading-relaxed">
        <p className="text-muted-foreground">
          Need to modify your flight time, date, or hotel destination?
        </p>
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Transfers</h3>
          <p className="text-muted-foreground">
            Cancellation is free of charge at least 24 hours before the agreed pickup or meeting time. For cancellations made less than 24 hours before that time, a cancellation fee of 25% of the total agreed booking price applies.
          </p>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Excursions</h3>
          <p className="text-muted-foreground break-words">
            {terms.cancellation}{" "}
            {terms.weatherRefund}
          </p>
        </div>
      </div>,
  linkText: "Cancellation Terms",
  to: "/TermsConditionsPage",
  icon: CalendarSync
}, {
  title: "Payment & Pricing",
  desc: <div className="space-y-3 text-sm sm:text-base leading-relaxed">
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Transfers</h3>
          <p className="text-muted-foreground">
            Prices per vehicle, paid in cash directly to the chauffeur upon arrival in any confirmed currency, with all highway tolls and parking included.
          </p>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-foreground text-sm">Excursions</h3>
          <p className="text-muted-foreground break-words">
            {terms.paymentOptions}
          </p>
        </div>
      </div>,
  linkText: "Payment Questions & Pricing Terms",
  to: "/TaxiServicePage",
  icon: CircleDollarSign
}, {
  title: "Contacting Support",
  desc: <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
        Connect with our live dispatch agents anytime day or night for travel assistance in Egypt.
      </p>,
  linkText: "Contact Options",
  to: "/BookYourPrivateTransferPage#talk-to-us-directly",
  icon: Headphones
}];
export default function HelpTopics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    once: true
  });
  return <section id="how-can-we-help" ref={ref} data-nav="light" className="relative bg-background text-foreground py-16 md:py-20 lg:py-24 overflow-hidden border-b border-border">
      <div className="max-w-[1136px] mx-auto px-6 sm:px-8">
        {/* Header Block */}
        <div className={`max-w-2xl mx-auto text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-primary mb-3">
            Customer Support
          </span>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
            How Can We Help?
          </h1>

          {/* Signature Hairline Rule with Turquoise Dot */}
          <div className="flex items-center justify-center gap-2 mt-6 mb-2">
            <div className="h-px w-20 bg-border" />
            <div className="size-1.5 rounded-full bg-primary" />
            <div className="h-px w-20 bg-border" />
          </div>
        </div>

        {/* 2x2 Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 md:mt-14">
          {helpTopics.map((topic, i) => {
          const Icon = topic.icon;
          return <div key={topic.title} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: isVisible ? `${60 * (i + 1)}ms` : "0ms"
          }}>
                <Card className="h-full border border-border bg-card hover:border-primary hover:shadow-md transition-all duration-200 flex flex-col justify-between p-6 sm:p-7">
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="size-10 rounded-md flex items-center justify-center bg-primary/10 border border-primary/25 text-primary shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground">
                        {topic.title}
                      </CardTitle>
                    </div>

                    <div className="mt-2">
                      {topic.desc}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-border/60">
                    <Link to={topic.to} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors group">
                      <span>{topic.linkText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {/* Bottom WhatsApp Inquiry Anchor */}
        <div className={`mt-12 md:mt-16 text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: isVisible ? "320ms" : "0ms"
      }}>
          <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-md bg-secondary border border-border/80 max-w-xl mx-auto">
            <span className="text-sm font-medium text-secondary-foreground text-center sm:text-left">
              Can't find what you're looking for?
            </span>
            <Button asChild className="bg-[#27BA2A] text-white hover:bg-[#22a025] h-10 px-5 text-sm font-medium shadow-xs">
              <Link to="https://wa.me/201010634627" newTab className="inline-flex items-center gap-2 text-white">
                <MessageCircle className="size-4" />
                <span>Chat With Support on WhatsApp</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}